import { Typography } from 'antd';
import React from 'react';

import SmallEmpty from '@/components/ux/SmallEmpty';
import { Button, Flex, FormInput, Icon, InfoBox, SlideOutPanel, SlideOutPanelHeader, Table, TBody, Td, Th, THead, Tr } from '@alpine-iq/ui-library';

import HoverQuestion from '@/components/HoverQuestion';
import CopyableText from '@/components/ux/CopyableText';
import { isEqual } from 'lodash';
import useQuery from '@/hooks/UseQuery';
import { getUserTextingSettings } from '@/pages/settings/textingSettings/texting-utils';
import { CampaignChannelType } from '@/types';
import { AttributeOut } from '@/pages/audiences/builder/components/TraitSelector';
import { asyncWrap, fetchAPI } from '@/api';
import { userUtils, utils } from '@/helpers';
import { CampaignBuilderMacros, Macro } from '@/pages/legacyCampaigns/builder/macros';
import { TextingSettings } from '@/pages/settings/textingSettings/TextingSettingsPage';

type PersonalizationMacroSelectorProps = {
	channel?: CampaignChannelType;
	whitelist?: string[];
	disabled?: boolean;
	trigger?: React.ReactNode;

	open?: boolean;
	setOpen?: (open: boolean) => void;

	loadMacros?: boolean;
	macroFallback?: Record<string, string>;
	setMacroFallback?: (macroFallback: Record<string, string>) => void;

	size?: 'small' | 'medium';
};

const modalKey = 'personalizationMacros';
export default function PersonalizationMacros(props: PersonalizationMacroSelectorProps) {
	const query = useQuery();
	const [open, _setOpen] = React.useState(props.open ?? query.get(modalKey) === 'true');
	const setOpen = (open: boolean) => {
		_setOpen(open);
		if (open) {
			const url = new URL(window.location.href);
			url.searchParams.set(modalKey, 'true');
			window.history.pushState({}, '', url.toString());
		} else {
			const url = new URL(window.location.href);
			url.searchParams.delete(modalKey);
			window.history.pushState({}, '', url.toString());
		}
		props.setOpen?.(open);
	};

	const [loading, setLoading] = React.useState(false);
	const [loaded, setLoaded] = React.useState(false);
	const [rawFileAttrs, setRawFileAttrs] = React.useState<AttributeOut[]>([]);
	const [currentMacroFallbacks, setCurrentMacroFallbacks] = React.useState<Record<string, string> | undefined>(props.macroFallback);

	React.useEffect(() => {
		if (props.loadMacros) {
			asyncWrap(async () => {
				const user = (await utils.auth.getUser(utils.uid)) ?? {};
				const settings: TextingSettings = getUserTextingSettings(user) ?? {};
				setCurrentMacroFallbacks(settings.macroFallback);
			});
		}
	}, [props.loadMacros]);

	React.useEffect(() => {
		setCurrentMacroFallbacks(props.macroFallback);
	}, [props.macroFallback]);

	React.useEffect(() => {
		if (open && !loaded) {
			asyncWrap(
				async () => {
					const fileAttrs = (await fetchAPI<AttributeOut[]>('/rawFileAttributes/:uid')) ?? [];
					setRawFileAttrs(fileAttrs);
				},
				{
					finallyCallback: () => setLoaded(true),
				},
			);
		}
	}, [open]);

	React.useEffect(() => {
		if (props.open) {
			setOpen(true);
		}
	}, [props.open]);

	const cleanedWhitelist = React.useMemo(() => {
		if (!props.whitelist) return null;
		return props.whitelist.map((w) => w.toLowerCase().replaceAll('}}', ''));
	}, [props.whitelist]);

	const hasPro = userUtils.hasPackageTier('marketing', 'pro');

	const allMacros = React.useMemo(() => {
		const byCategory: Record<string, Macro[]> = Object.entries(CampaignBuilderMacros)
			.filter(([key, value]) => {
				const dontShowInMacroList = typeof value.dontShowInMacroList === 'function' ? value.dontShowInMacroList() : value.dontShowInMacroList;
				if (dontShowInMacroList) return false;
				if (!hasPro && value.requiredPermissions?.marketing === 'pro') return false;

				if (!cleanedWhitelist || cleanedWhitelist.length === 0) return true;
				const cleanedKey = key.toLowerCase().replaceAll('7-🔥}}', '').replaceAll('}}', '');
				const cleanedMacro = value.macro.toLowerCase().replaceAll('7-🔥}}', '').replaceAll('}}', '');
				const isWhitelisted = cleanedWhitelist.includes(cleanedMacro) || cleanedWhitelist.includes(cleanedKey);
				return isWhitelisted;
			})
			.reduce(
				(acc, [_, value]) => {
					// Map category to macros
					const category = value.category || 'Other';
					if (!acc[category]) acc[category] = [];
					acc[category].push(value);
					return acc;
				},
				{} as Record<string, Macro[]>,
			);

		// Now we insert the raw file attributes under category "File Attributes"
		if (rawFileAttrs.length > 0) {
			byCategory['Custom Attributes'] = rawFileAttrs.map((attr) => {
				return {
					category: 'Custom Attributes',
					tooltip: `"${attr.label}" from file upload`,
					macro: `{{${attr.value}}}`,
				} as Macro;
			});
		}

		return (
			Object.keys(byCategory)
				.sort((a, b) => byCategory[b].length - byCategory[a].length)
				// Flatten this array into items with category as a key
				// So getting an array of { ...value, category: key }
				.flatMap((category) => byCategory[category].map((macro) => ({ ...macro, category })))
		);
	}, [cleanedWhitelist, hasPro, rawFileAttrs]);

	const showFallbacks = !!props.setMacroFallback || props.loadMacros;

	const handleSave = () =>
		asyncWrap(
			async () => {
				if (isEqual(currentMacroFallbacks, props.macroFallback)) return utils.showErr('No changes made');

				setLoading(true);

				const user = await utils.auth.getUser(utils.uid);
				const settings = getUserTextingSettings(user);

				settings.macroFallback = currentMacroFallbacks ?? {};

				await fetchAPI('/users/textingSettings/:uid', {
					method: 'PUT',
					payload: settings,
				});

				setCurrentMacroFallbacks(currentMacroFallbacks);
				props.setMacroFallback?.(currentMacroFallbacks ?? {});
			},
			{
				finallyCallback() {
					setLoading(false);
				},
			},
		);

	return (
		<>
			{(!props.setOpen || !!props.trigger) && (
				<Flex
					onClick={() => {
						if (props.disabled) return;
						setOpen(!open);
					}}
				>
					{props.trigger || (
						<Button
							disabled={props.disabled}
							size={props.size ?? 'medium'}
						>
							<Flex align="center">
								<Icon
									type="PLUS"
									height={20}
									width={20}
								/>
								Personalization
							</Flex>
						</Button>
					)}
				</Flex>
			)}

			<SlideOutPanel
				open={open}
				usePortal
				onClose={() => setOpen(false)}
			>
				<SlideOutPanelHeader
					onClose={() => setOpen(false)}
					centerContent={<Typography css={{ fontWeight: 700 }}>Personalization macro</Typography>}
					actions={
						showFallbacks && (
							<Button
								variant="primary"
								type="submit"
								loading={loading}
								onClick={handleSave}
							>
								Save
							</Button>
						)
					}
				/>
				<Flex
					column
					padding={40}
					gap={24}
				>
					<InfoBox
						type="info"
						closeable={false}
					>
						Copy and paste a macro below to let us personalize your message. Messages won't be sent if the required data for the macro is missing.
					</InfoBox>
					<Table loadingColumns={2}>
						<THead>
							<Tr>
								<Th>MACRO</Th>
								<Th>CATEGORY</Th>
								<Th>DESCRIPTION</Th>
								{showFallbacks && (
									<Th>
										<Flex align="center">
											FALLBACK
											<HoverQuestion title="Fallback values are used in place of empty or missing data in order to ensure your message is sent." />
										</Flex>
									</Th>
								)}
							</Tr>
						</THead>
						<TBody>
							{(allMacros ?? []).map((attribute) => (
								<Tr key={attribute.macro}>
									<Td>
										<CopyableText copyText={attribute.macro}>{attribute.macro}</CopyableText>
									</Td>
									<Td>{attribute.category}</Td>
									<Td>{attribute.description}</Td>
									{showFallbacks && (
										<Td>
											{attribute.fallback && (
												<FormInput
													disabled={props.disabled || !attribute.fallback}
													value={currentMacroFallbacks?.[attribute.macro?.toLowerCase()] || ''}
													onChange={(e) => {
														const newFallbacks = { ...currentMacroFallbacks };
														if (!newFallbacks) return;
														if (e.target.value) {
															newFallbacks[attribute.macro?.toLowerCase()] = e.target.value;
														} else {
															delete newFallbacks[attribute.macro?.toLowerCase()];
														}
														setCurrentMacroFallbacks(newFallbacks);
													}}
												/>
											)}
										</Td>
									)}
								</Tr>
							))}
							{!allMacros.length && <SmallEmpty>No Macros</SmallEmpty>}
						</TBody>
					</Table>
				</Flex>
			</SlideOutPanel>
		</>
	);
}
